import React from "react";
import { Search, Calendar, ChevronDown, User, CheckCircle2, XCircle, Check, X, Loader2, MapPin, ChevronRight } from "lucide-react";
import { clsx } from "clsx";
import { useVisitManagePage } from "@hooks"; 
import { PageHeader, FilterBar, DataTable } from "@components"; 

export function VisitManagement() {
  const {
    filters, setFilters, query, page,
    editingRows, startEdit, cancelEdit, updateEditRow, saveRow, getKey, toggleExecution, savingRowIds,
    expandedRows, toggleExpand,
    onSearch, auth
  } = useVisitManagePage();

  const handleReset = () => {
    setFilters({ assignee: '', companyName: '', startDate: '', endDate: '', pageNum: 1 });
  };

  const handleCancel = (e, rowId) => {
    e.stopPropagation();
    cancelEdit(rowId);
  };

  const initialHelpText = `배정된 사업장에 대한 방문 일정(예정일/회차)과 실시 여부를 관리하는 페이지입니다.

[주의사항]
관리자(Admin)가 아닌 일반 사용자는 본인이 담당자1(정)로 지정된 사업장만 조회 및 수정할 수 있습니다.

[이용 순서]
1. 일정 수정: 하위 목록의 '방문 예정일' 또는 '방문 회차'를 클릭하여 수정 후 [V] 버튼으로 저장합니다.
2. 실시 여부 토글: 방문이 완료되면 [미실시] 버튼을 클릭하여 [실시]로 변경합니다. (이전 회차가 미실시면 변경 불가)
※ [실시]를 [미실시]로 되돌릴 때, 이미 업로드된 파일이 있거나 이후 회차가 실시 상태면 변경할 수 없습니다.`;

  const tableColumns = [
    { label: "", width: "w-12" }, // 펼치기 아이콘
    { label: "방문 담당자 1" },
    { label: "방문 담당자 2" },
    { label: "등록 지사" },
    { label: "회사명", className: "min-w-[160px]" },
    { label: "대표자" },
    { label: "근로자 수", align: "text-right" },
    { label: "업종" },
    { label: "주소", className: "min-w-[200px]" },
  ];

  const isAdmin = auth?.role === 'ROLE_ADMIN';

  return (
    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500 w-full max-w-full">
      <PageHeader 
        icon={MapPin} iconBgColor="bg-blue-600" title="방문 일정 관리" 
        description="수행요원별 사업장 방문 일정과 실시 여부를 관리합니다." 
        auth={auth} helpId="visit_management" initialHelpText={initialHelpText}
      />

      <FilterBar onReset={handleReset} isSearching={query.isFetching}>
        <div className="space-y-1.5 flex-1 max-w-[200px] min-w-[150px]">
          <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">회사명</label>
          <input type="text" placeholder="회사명 입력" className="h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm focus:ring-1 focus:ring-blue-500" value={filters.companyName} onChange={(e) => setFilters(p => ({ ...p, companyName: e.target.value }))} onKeyDown={(e) => e.key === 'Enter' && onSearch()} />
        </div>

        {isAdmin && (
          <div className="space-y-1.5 flex-1 max-w-[200px] min-w-[150px]">
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide flex items-center gap-1">방문 담당자 1</label>
            <div className="relative">
              <input type="text" placeholder="담당자명 검색" className="h-10 w-full rounded-lg border border-slate-300 bg-white pl-10 pr-4 text-sm focus:ring-1 focus:ring-blue-500" value={filters.assignee} onChange={(e) => setFilters(p => ({ ...p, assignee: e.target.value }))} onKeyDown={(e) => e.key === 'Enter' && onSearch()} />
              <User className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            </div>
          </div>
        )}

        <div className="space-y-1.5 flex-1 max-w-md min-w-[300px]">
          <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">방문 기간</label>
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <input type="date" value={filters.startDate} onChange={(e) => setFilters(p => ({ ...p, startDate: e.target.value }))} className="h-10 w-full rounded-lg border border-slate-300 bg-white pl-10 pr-2 text-sm focus:ring-1 focus:ring-blue-500" />
              <Calendar className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400 pointer-events-none" />
            </div>
            <span className="text-slate-400">~</span>
            <div className="relative flex-1">
              <input type="date" value={filters.endDate} onChange={(e) => setFilters(p => ({ ...p, endDate: e.target.value }))} className="h-10 w-full rounded-lg border border-slate-300 bg-white pl-10 pr-2 text-sm focus:ring-1 focus:ring-blue-500" />
              <Calendar className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400 pointer-events-none" />
            </div>
          </div>
        </div>
      </FilterBar>

      <div className={clsx(
              "[&>div]:max-h-[600px] [&>div]:flex [&>div]:flex-col",
              "[&>div>div.overflow-x-auto]:flex-1 [&>div>div.overflow-x-auto]:overflow-y-auto [&>div>div.overflow-x-auto]:custom-scrollbar",
              "[&>div>div.overflow-x-auto>table>thead]:sticky [&>div>div.overflow-x-auto>table>thead]:top-0 [&>div>div.overflow-x-auto>table>thead]:z-30",
              "[&>div>div.overflow-x-auto>table>thead>tr>th]:bg-slate-50 [&>div>div.overflow-x-auto>table>thead>tr>th]:shadow-[inset_0_-1px_0_0_#e2e8f0]"
            )}>
        <DataTable 
          columns={tableColumns}
          page={page}
          itemName="방문 그룹"
          onPageChange={(newPage) => setFilters(p => ({ ...p, pageNum: newPage }))}
          emptyMessage="방문 일정이 없습니다."
          renderRow={(group) => {
            const groupId = group.bizMgtNo;
            const isExpanded = expandedRows[groupId];
            const canEditGroup = isAdmin || auth?.staffId === group.staffId1;

            return (
              <React.Fragment key={groupId}>
                {/* 🌟 1. 통합(그룹) 행 */}
                <tr 
                  onClick={() => toggleExpand(groupId)}
                  className="group/row bg-white font-medium hover:bg-blue-50/50 transition-colors cursor-pointer border-b border-slate-200"
                >
                  <td className="px-4 py-4 text-center text-slate-400 group-hover/row:text-blue-600 transition-colors">
                    {isExpanded ? <ChevronDown className="h-5 w-5 mx-auto" /> : <ChevronRight className="h-5 w-5 mx-auto" />}
                  </td>
                  <td className="px-6 py-4">
                    <span className="inline-flex items-center rounded-full bg-blue-100 px-2.5 py-1 text-xs font-semibold text-blue-700">{group.staffNm1 || '-'}</span>
                  </td>
                  <td className="px-6 py-4">
                    <span className="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-700">{group.staffNm2 || '미지정'}</span>
                  </td>
                  <td className="px-6 py-4 text-slate-700">{group.branchName || '-'}</td>
                  <td className="px-6 py-4 font-bold text-slate-900">{group.companyName || '-'}</td>
                  <td className="px-6 py-4 text-slate-700">{group.ceoName || '-'}</td>
                  <td className="px-6 py-4 text-slate-700 text-right">{group.employeeCount?.toLocaleString() || '-'}</td>
                  <td className="px-6 py-4 text-slate-700">{group.businessType || '-'}</td>
                  <td className="px-6 py-4 text-slate-500 truncate max-w-[200px]" title={group.address}>{group.address || '-'}</td>
                </tr>

                {/* 🌟 2. 개별 회차(Sub-rows) 독립 테이블 */}
                {isExpanded && (
                  <tr className="bg-slate-50/60 border-b-2 border-slate-200 shadow-[inset_0px_5px_8px_-6px_rgba(0,0,0,0.1)]">
                    <td colSpan={9} className="p-0">
                      <div className="py-4 pl-[4.5rem] pr-6">
                        <div className="rounded-lg border border-slate-200 bg-white overflow-hidden shadow-sm">
                          <table className="w-full text-sm">
                            
                            {/* 🌟 (수정) 회사명 너비 축소(200px) 및 방문일자/회차 너비 확대 */}
                            <thead className="bg-slate-100/80 border-b border-slate-200 text-slate-500 text-xs font-semibold uppercase tracking-wide">
                              <tr>
                                <th className="px-6 py-3 text-center w-32 border-r border-slate-200">방문 담당자 1</th>
                                <th className="px-6 py-3 text-left w-[280px] border-r border-slate-200">회사명</th>
                                <th className="px-6 py-3 text-center w-[240px] bg-blue-50 border-x border-blue-200 text-blue-700 font-bold shadow-sm">방문 일자</th>
                                <th className="px-6 py-3 text-center w-[200px] bg-blue-50 border-r border-blue-200 text-blue-700 font-bold shadow-sm">방문 회차</th>
                                <th className="px-6 py-3 text-center w-[140px] bg-blue-50 border-r border-blue-200 text-blue-700 font-bold shadow-sm">실시 여부</th>
                              </tr>
                            </thead>
                            
                            <tbody className="divide-y divide-slate-100">
                              {group.rounds?.map((round) => {
                                const roundId = round.assignNo;
                                const rEditData = editingRows[roundId] || {};
                                const isExecuted = round.executeDtm != null && round.executeDtm.trim() !== '';

                                return (
                                  <tr key={roundId} className="hover:bg-blue-50/20 transition-colors">
                                    
                                    {/* 방문 담당자 1 (고정) */}
                                    <td className="px-6 py-2 text-center border-r border-slate-200 bg-slate-50/30">
                                      <span className="inline-flex items-center rounded-full bg-blue-100 px-2.5 py-1 text-xs font-semibold text-blue-700">{group.staffNm1 || '-'}</span>
                                    </td>
                                    
                                    {/* 🌟 (수정) 회사명 (너비 제한 및 말줄임 추가) */}
                                    <td className="px-6 py-2 font-bold text-slate-900 border-r border-slate-200 bg-slate-50/30 truncate max-w-[280px]" title={group.companyName}>
                                      {group.companyName || '-'}
                                    </td>

                                    {/* 예정일 (인라인 편집, 파란 배경 유지) */}
                                    <td className="px-6 py-2 border-r border-blue-100 bg-blue-50/40" onClick={(e) => e.stopPropagation()}>
                                      {rEditData.editType === 'visitDate' ? (
                                        <div className="flex items-center gap-1 animate-in fade-in zoom-in duration-200">
                                          <input type="date" value={rEditData.visitDate || ''} onChange={(e) => updateEditRow(roundId, { visitDate: e.target.value })} className="h-8 w-full rounded border border-blue-300 bg-white px-2 text-xs focus:ring-2 focus:ring-blue-200" />
                                          <button onClick={() => saveRow(round)} className="p-1 rounded bg-green-100 text-green-700 hover:bg-green-200"><Check className="h-3 w-3" /></button>
                                          <button onClick={(e) => handleCancel(e, roundId)} className="p-1 rounded bg-red-100 text-red-700 hover:bg-red-200"><X className="h-3 w-3" /></button>
                                        </div>
                                      ) : (
                                        <div onClick={(e) => { e.stopPropagation(); if(!canEditGroup) return alert("수정 권한이 없습니다."); startEdit(round, 'visitDate'); }} className={clsx("flex items-center justify-between gap-2 rounded px-2 py-1 text-sm text-slate-700 border border-transparent transition-all", canEditGroup && "cursor-pointer hover:border-blue-300 hover:bg-white")}>
                                          <span className={round.visitDate ? "" : "text-slate-400"}>{round.visitDate ? `${round.visitDate}` : '미정'}</span>
                                          {canEditGroup && <ChevronDown className="h-3 w-3 text-slate-300" />}
                                        </div>
                                      )}
                                    </td>

                                    {/* 회차 (인라인 편집, 파란 배경 유지) */}
                                    <td className="px-6 py-2 text-center border-r border-blue-100 bg-blue-50/40" onClick={(e) => e.stopPropagation()}>
                                      {rEditData.editType === 'visitRound' ? (
                                        <div className="flex items-center gap-1 animate-in fade-in zoom-in duration-200">
                                          <select value={rEditData.visitRound || 1} onChange={(e) => updateEditRow(roundId, { visitRound: Number(e.target.value) })} className="h-8 w-full rounded border border-blue-300 bg-white px-2 text-xs focus:ring-2 focus:ring-blue-200">
                                            {[1, 2, 3, 4, 5].map(num => <option key={num} value={num}>{num}회차</option>)}
                                          </select>
                                          <button onClick={() => saveRow(round)} className="p-1 rounded bg-green-100 text-green-700 hover:bg-green-200"><Check className="h-3 w-3" /></button>
                                          <button onClick={(e) => handleCancel(e, roundId)} className="p-1 rounded bg-red-100 text-red-700 hover:bg-red-200"><X className="h-3 w-3" /></button>
                                        </div>
                                      ) : (
                                        <div onClick={(e) => { e.stopPropagation(); if(!canEditGroup) return alert("수정 권한이 없습니다."); startEdit(round, 'visitRound'); }} className={clsx("flex items-center justify-center gap-2 rounded px-2 py-1 text-sm font-bold text-blue-700 border border-transparent transition-all", canEditGroup && "cursor-pointer hover:border-blue-300 hover:bg-white")}>
                                          <span>{round.visitRound}회차</span>
                                          {canEditGroup && <ChevronDown className="h-3 w-3 text-blue-300" />}
                                        </div>
                                      )}
                                    </td>

                                    {/* 실시 여부 (토글, 파란 배경 유지) */}
                                    <td className="px-6 py-2 text-center bg-blue-50/40">
                                      <button
                                        onClick={() => { if(!canEditGroup) return alert("권한이 없습니다."); toggleExecution(round); }}
                                        disabled={savingRowIds[roundId] || !canEditGroup}
                                        className={clsx(
                                          "relative inline-flex h-8 w-full max-w-[120px] items-center justify-center gap-2 rounded-md border text-xs font-semibold shadow-sm transition-all duration-200 mx-auto",
                                          isExecuted ? "bg-green-50 border-green-200 text-green-700 hover:bg-green-100" : "bg-white border-slate-200 text-slate-500 hover:bg-slate-50 hover:text-slate-700",
                                          (savingRowIds[roundId] || !canEditGroup) && "opacity-50 cursor-not-allowed"
                                        )}
                                      >
                                        {savingRowIds[roundId] ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 
                                          isExecuted ? (
                                          <><div className="flex h-4 w-4 items-center justify-center rounded-full bg-green-200 text-green-700"><CheckCircle2 className="h-2.5 w-2.5" /></div><span>실시 완료</span></>
                                        ) : (
                                          <><div className="flex h-4 w-4 items-center justify-center rounded-full bg-slate-100 text-slate-400"><XCircle className="h-2.5 w-2.5" /></div><span>미실시</span></>
                                        )}
                                      </button>
                                    </td>

                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          }}
        />
      </div>
    </div>
  );
}